import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Card, CardActionArea, Collapse, Typography, IconButton, Divider,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress,
} from '@mui/material';
import { ExpandMore, Sensors, Hub, Close } from '@mui/icons-material';
import { useAuthStore } from '../../stores/auth.store';
import { useReportDefaults } from '../../hooks/useReportDefaults';
import { resolveDatePreset } from '../../utils/date-presets';
import { dayRangeParams } from '../../utils/timezone';
import { trackingService } from '../../services/tracking.service';
import type { TrackingStatsRow } from '../../services/tracking.service';

interface Row { name: string; profit: number; revenue: number; cost: number; roas: number }

const money = (n: number) => `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString()}`;
const profitColor = (n: number) => (n < 0 ? 'error.main' : 'success.main');
const kMoney = (n: number) => {
  const a = Math.abs(n);
  const s = a >= 1000 ? `$${(a / 1000).toFixed(a >= 10000 ? 0 : 1)}k` : `$${Math.round(a)}`;
  return `${n < 0 ? '-' : ''}${s}`;
};
const toRow = (r: TrackingStatsRow): Row => ({
  name: r.name || '(none)', profit: r.profit, revenue: r.revenue, cost: r.cost, roas: r.roas,
});

function BreakdownTable({ title, icon, rows }: { title: string; icon: React.ReactNode; rows: Row[] }) {
  return (
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        {icon}
        <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>{title}</Typography>
      </Box>
      <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5, borderColor: 'divider' } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Name</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Profit</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Revenue</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>Cost</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>ROAS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={5} sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>No data yesterday.</TableCell></TableRow>
          )}
          {rows.map((r) => (
            <TableRow key={r.name} hover>
              <TableCell sx={{ fontSize: '0.75rem' }}>{r.name}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: profitColor(r.profit) }}>{money(r.profit)}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{money(r.revenue)}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{money(r.cost)}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{r.roas.toFixed(2)}×</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

interface Stat { profit: number; revenue: number; cost: number; roas: number }

/** One overview card. Clickable (a workspace) when `onClick` is set; a static total when `emphasis`. */
function StatCard({ name, stat, loading, selected = false, emphasis = false, onClick }: {
  name: string; stat: Stat; loading: boolean; selected?: boolean; emphasis?: boolean; onClick?: () => void;
}) {
  const body = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: emphasis ? 700 : 600, color: 'text.secondary' }} noWrap>{name}</Typography>
        <ExpandMore sx={{ fontSize: '0.95rem', color: 'text.secondary', visibility: onClick ? 'visible' : 'hidden', transform: selected ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: loading ? 'text.disabled' : profitColor(stat.profit), lineHeight: 1 }}>
          {loading ? '—' : money(stat.profit)}
        </Typography>
        <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {loading ? ' ' : `rev ${kMoney(stat.revenue)} · cost ${kMoney(stat.cost)} · ${stat.roas.toFixed(2)}×`}
        </Typography>
      </Box>
    </>
  );
  const sx = { px: 1.25, py: 0.75, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.25 } as const;
  return (
    <Card variant="outlined" sx={{
      minWidth: 215, flex: '0 0 auto',
      bgcolor: emphasis ? 'action.hover' : undefined,
      borderColor: selected ? 'primary.main' : 'divider',
      borderWidth: selected ? 2 : 1,
      boxShadow: selected ? 1 : 0,
    }}>
      {onClick ? <CardActionArea onClick={onClick} sx={sx}>{body}</CardActionArea> : <Box sx={sx}>{body}</Box>}
    </Card>
  );
}

/** Admin-only strip: yesterday per workspace; click a card for its TS + network breakdown. */
export default function WorkspaceOverviewStrip() {
  const role = useAuthStore((s) => s.user?.role);
  const workspaces = useAuthStore((s) => s.user?.workspaces);
  const wanted = useAuthStore((s) => s.user?.settings?.tracking?.show_workspace_strip) !== false;
  const { timezone } = useReportDefaults();
  const [selected, setSelected] = useState<string | null>(null);

  // Yesterday, in the viewer's report timezone — same convention the Tracking grid uses.
  const { date_from, date_to } = useMemo(() => dayRangeParams(resolveDatePreset('yesterday')), []);

  const show = wanted && role === 'admin' && (workspaces?.length ?? 0) >= 2;

  const overview = useQuery({
    queryKey: ['ws-overview', date_from, date_to, timezone],
    queryFn: () => trackingService.getWorkspaceOverview({ date_from, date_to, timezone }),
    enabled: show,
    staleTime: 5 * 60_000,
  });

  const breakdown = useQuery({
    queryKey: ['ws-breakdown', selected, date_from, date_to, timezone],
    queryFn: async () => {
      const [ts, nw] = await Promise.all([
        trackingService.getStats({ group_by: 'traffic_source_id', date_from, date_to, timezone, limit: 100 }, { workspaceId: selected! }),
        trackingService.getStats({ group_by: 'network_id', date_from, date_to, timezone, limit: 100 }, { workspaceId: selected! }),
      ]);
      return { traffic_sources: ts.rows.map(toRow), networks: nw.rows.map(toRow) };
    },
    enabled: show && !!selected,
    staleTime: 5 * 60_000,
  });

  if (!show) return null;

  // Rows arrive sorted by profit desc; keep that order, then the workspaces with no activity.
  const byId = new Map((overview.data?.rows ?? []).map((r) => [r.group_key, r]));
  const nameById = new Map((workspaces ?? []).map((w) => [w.id, w.name]));
  const orderedIds = [
    ...(overview.data?.rows ?? []).map((r) => r.group_key).filter((id) => nameById.has(id)),
    ...(workspaces ?? []).map((w) => w.id).filter((id) => !byId.has(id)),
  ];
  const cards = orderedIds.map((id) => {
    const r = byId.get(id);
    return { id, name: nameById.get(id) ?? '', profit: r?.profit ?? 0, revenue: r?.revenue ?? 0, cost: r?.cost ?? 0, roas: r?.roas ?? 0 };
  });
  const active = cards.find((c) => c.id === selected) || null;
  const loading = overview.isLoading;

  // The overview response already carries the aggregate; roas is a formatted string, so recompute it.
  const t = overview.data?.totals;
  const total: Stat | null = t
    ? { profit: t.profit, revenue: t.revenue, cost: t.cost, roas: t.cost > 0 ? t.revenue / t.cost : 0 }
    : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
        {total && <StatCard name="All workspaces" stat={total} loading={loading} emphasis />}
        {cards.map((w) => (
          <StatCard
            key={w.id}
            name={w.name}
            stat={w}
            loading={loading}
            selected={w.id === selected}
            onClick={() => setSelected(w.id === selected ? null : w.id)}
          />
        ))}
      </Box>

      <Collapse in={!!active}>
        {active && (
          <Card variant="outlined" sx={{ mt: 1.5, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>{active.name}</Typography>
              <Chip size="small" label="yesterday" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              <Typography sx={{ fontSize: '0.8rem', color: profitColor(active.profit), fontWeight: 600 }}>
                {money(active.profit)} profit
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <IconButton size="small" onClick={() => setSelected(null)}><Close fontSize="small" /></IconButton>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {breakdown.isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, color: 'text.secondary' }}>
                <CircularProgress size={16} /><Typography variant="body2">Loading breakdown…</Typography>
              </Box>
            ) : breakdown.isError ? (
              <Typography variant="body2" color="error.main" sx={{ py: 1 }}>Couldn't load this workspace's breakdown.</Typography>
            ) : (
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <BreakdownTable title="Traffic sources" icon={<Sensors fontSize="small" color="action" />} rows={breakdown.data?.traffic_sources ?? []} />
                <BreakdownTable title="Networks" icon={<Hub fontSize="small" color="action" />} rows={breakdown.data?.networks ?? []} />
              </Box>
            )}
          </Card>
        )}
      </Collapse>
    </Box>
  );
}
